import { calculateProportionalTolerance, getEffectiveTolerance } from "./toleranceCalculator";

/**
 * Gets tile ID from a clientPosition via math operations. (for pointerEvents)
 * @param {number} mx clientX
 * @param {number} my clientY
 * @param {DOMRect} domRect node boundingClientRect
 * @param {number} gridSize amount of tiles at row
 * @param {number|null} tolerance margin in pixels around the grid (null for auto-calculation)
 * @returns {number|null} tile ID at grid, or null if too far outside
 */
export function fetchByPos(mx, my, domRect, gridSize, tolerance = null) {
  if (!isFinite(mx) || !isFinite(my) || !domRect || !gridSize) {
    return null;
  }

  const { x, y, width, height } = domRect;
  
  if (!isFinite(x) || !isFinite(y) || !isFinite(width) || !isFinite(height) ||
      width <= 0 || height <= 0 || gridSize <= 0) {
    return null;
  }

  if (tolerance === null) {
    const proportionalTolerance = calculateProportionalTolerance(domRect, gridSize);
    tolerance = getEffectiveTolerance(proportionalTolerance);
  }

  if (!isFinite(tolerance) || tolerance < 0) {
    tolerance = 0; 
  }

  if (mx < x - tolerance || mx > x + width + tolerance ||
      my < y - tolerance || my > y + height + tolerance) {
    return null;
  }

  const clampedX = Math.max(x, Math.min(x + width - 1, mx));
  const clampedY = Math.max(y, Math.min(y + height - 1, my));

  const tileWidth = width / gridSize;
  const tileHeight = height / gridSize;
  
  if (!isFinite(tileWidth) || !isFinite(tileHeight) || tileWidth <= 0 || tileHeight <= 0) {
    
    return null;
  }

  const xTile = Math.floor((clampedX - x) / tileWidth);
  const yTile = Math.floor((clampedY - y) / tileHeight);

  if (!isFinite(xTile) || !isFinite(yTile)) {
    
    return null;
  }

  const finalXTile = Math.max(0, Math.min(gridSize - 1, xTile));
  const finalYTile = Math.max(0, Math.min(gridSize - 1, yTile));

  const tileId = finalYTile * gridSize + finalXTile;
  
  if (!isFinite(tileId) || tileId < 0) {
    
    return null;
  }

  return tileId;
}

/**
 * Gets tile ID from positions relative to the domRect. (for fetching after calculations)
 * @param {number} mx mouse clientX (relative to grid)
 * @param {number} my mouse clientY (relative to grid)
 * @param {DOMRect} domRect node boundingClientRect
 * @param {number} gridSize amount of tiles at row
 * @param {number|null} tolerance margin in pixels around the grid (null for auto-calculation)
 * @returns {number|null} tile ID at grid, or null if too far outside
 */
export function fetchByLocalPos(mx, my, domRect, gridSize, tolerance = null) {
  if (!isFinite(mx) || !isFinite(my) || !domRect || !gridSize) {
    return null;
  }

  const { width, height } = domRect;
  
  if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0 || gridSize <= 0) {
    return null;
  }

  if (tolerance === null) {
    const proportionalTolerance = calculateProportionalTolerance(domRect, gridSize);
    tolerance = getEffectiveTolerance(proportionalTolerance);
  }

  if (!isFinite(tolerance) || tolerance < 0) {
    
    tolerance = 0; 
  }

  if (mx < -tolerance || mx > width + tolerance ||
      my < -tolerance || my > height + tolerance) {
    return null;
  }

  const clampedX = Math.max(0, Math.min(width - 1, mx));
  const clampedY = Math.max(0, Math.min(height - 1, my));

  const tileWidth = width / gridSize;
  const tileHeight = height / gridSize;
  
  if (!isFinite(tileWidth) || !isFinite(tileHeight) || tileWidth <= 0 || tileHeight <= 0) {
    
    return null;
  }

  const xTile = Math.floor(clampedX / tileWidth);
  const yTile = Math.floor(clampedY / tileHeight);

  if (!isFinite(xTile) || !isFinite(yTile)) {
    
    return null;
  }

  const finalXTile = Math.max(0, Math.min(gridSize - 1, xTile));
  const finalYTile = Math.max(0, Math.min(gridSize - 1, yTile));

  const tileId = finalYTile * gridSize + finalXTile;
  
  if (!isFinite(tileId) || tileId < 0) {
    
    return null;
  }

  return tileId;
}
